import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/auth/Login';
import OAuthCallback from './pages/auth/OAuthCallback';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import VerifyEmail from './pages/auth/VerifyEmail';
import SecurityNotMe from './pages/auth/SecurityNotMe';
import NewSignIn from './pages/auth/NewSignIn';
import EmailPreferences from './pages/auth/EmailPreferences';
import AcceptInvitation from './pages/auth/AcceptInvitation';
import AcceptLegalDocuments from './pages/auth/AcceptLegalDocuments';
import PrivacyNotice from './pages/PrivacyNotice';
import ChangePassword from './pages/auth/ChangePassword';
import MfaEnroll from './pages/auth/MfaEnroll';
import RequestAccess from './pages/auth/RequestAccess';
import Contact from './pages/Contact';
import Maintenance from './pages/Maintenance';
import ManagerDashboard from './pages/Dashboard';
import ManagerInvitations from './pages/manager/Invitations';
import AdminStaff from './pages/admin/Staff';
import AdminClients from './pages/admin/Clients';
import AdminLeads from './pages/admin/Leads';
import AdminMaintenance from './pages/admin/Maintenance';
import AdminCommitmentConfigs from './pages/admin/CommitmentConfigs';
import AdminConcentrationAccounts from './pages/admin/ConcentrationAccounts';
import AdminEmailTemplates from './pages/admin/EmailTemplates';
import AdminContractTemplates from './pages/admin/ContractTemplates';
import AdminConfigHealth from './pages/admin/ConfigHealth';
import AdminProductAccess from './pages/admin/ProductAccess';
import AdminProducts from './pages/admin/Products';
import AdminJurisdictions from './pages/admin/Jurisdictions';
import AdminDocumentWording from './pages/admin/DocumentWording';
import RegulatoryReports from './pages/manager/RegulatoryReports';
import AdminAuditLogExport from './pages/admin/AuditLogExport';
import AdminDashboard from './pages/admin/AdminDashboard';
import AgentDashboard from './pages/agent/Dashboard';
import AgentReviewQueue from './pages/agent/ReviewQueue';
import AgentAvailability from './pages/agent/Availability';
import AgentAppointments from './pages/agent/Appointments';
import AgentDeposits from './pages/agent/Deposits';
import AgentCommitments from './pages/agent/Commitments';
import AgentReversals from './pages/agent/Reversals';
import AgentMyClients from './pages/agent/MyClients';
import ClientDetail from './pages/agent/ClientDetail';
import CallGuide from './pages/agent/CallGuide';
import ManagerReviewQueue from './pages/manager/ReviewQueue';
import ManagerAppointments from './pages/manager/Appointments';
import ManagerDeposits from './pages/manager/Deposits';
import ManagerCommitments from './pages/manager/Commitments';
import ManagerProposalCampaigns from './pages/manager/ProposalCampaigns';
import StaffWithdrawals from './pages/staff/Withdrawals';
import ManagerComplianceAlerts from './pages/manager/ComplianceAlerts';
import ManagerSanctions from './pages/manager/SanctionsScreenings';
import ManagerSars from './pages/manager/Sars';
import ManagerReversals from './pages/manager/Reversals';
import ManagerTeam from './pages/manager/Team';
import StaffCaseDetail from './pages/staff/CaseDetail';
import StaffAttributeDeposit from './pages/staff/AttributeDeposit';
import StaffPayouts from './pages/staff/Payouts';
import StaffReconciliation from './pages/staff/Reconciliation';
import StaffClientRisk from './pages/staff/ClientRisk';
import StaffRedemptions from './pages/staff/Redemptions';
import AdminOfferingDocuments from './pages/admin/OfferingDocuments';
import ManagerRegDPackage from './pages/manager/RegDPackage';
import AgentOfferingQuestions from './pages/agent/OfferingQuestions';
import AdminBlockedCountries from './pages/admin/BlockedCountries';
import OperationalReminders from './pages/admin/OperationalReminders';
import BankAccountChecks from './pages/staff/BankAccountChecks';
import SourceAccounts from './pages/staff/SourceAccounts';
import TransferCases from './pages/manager/TransferCases';
import SuspicionAvisos from './pages/manager/SuspicionAvisos';
import UmaValue from './pages/manager/UmaValue';
import OfacBlocking from './pages/manager/OfacBlocking';
import RnieRegistrations from './pages/manager/RnieRegistrations';
import ManagerDataRequests from './pages/manager/DataRequests';
import ClientDataRequests from './pages/client/DataRequests';
import StaffDepositReversals from './pages/staff/DepositReversals';
import ClientDashboard from './pages/client/Dashboard';
import ClientBeneficiaries from './pages/client/Beneficiaries';
import ClientContracts from './pages/client/Contracts';
import ClientContractCase from './pages/client/ContractCase';
import ClientOnboarding from './pages/client/Onboarding';
import ClientPortfolio from './pages/client/Portfolio';
import ClientFund from './pages/client/Fund';
import ClientWithdraw from './pages/client/Withdraw';
import ClientProductDetail from './pages/client/ProductDetail';
import Profile from './pages/Profile';
import Simulator from './pages/Simulator';
import Terms from './pages/Terms';
import NotFound from './pages/NotFound';
import { useAuthStore } from './stores/authStore';
import { dashboardPathForRole } from './lib/roleRouting';
import MainLayout from './components/layout/MainLayout';
import MaintenanceBanner from './components/maintenance/MaintenanceBanner';
import { useActiveMaintenance } from './hooks/useActiveMaintenance';

/**
 * Shows the maintenance banner on public pages (login, forgot-password, etc.).
 * Authenticated users already see it via MainLayout's sticky header; this
 * avoids a duplicate by only rendering when the user is not signed in.
 */
function GlobalMaintenanceBanner(): JSX.Element | null {
  const { isAuthenticated } = useAuthStore();
  // Public, unauthenticated feed (#54): poll /maintenance/status only while
  // signed out so the login page shows the banner. The authenticated banner
  // is owned by MainLayout's sticky header.
  const maintenance = useActiveMaintenance(!isAuthenticated, true);
  if (isAuthenticated) return null;
  return <MaintenanceBanner data={maintenance} />;
}

/** Redirects "/" and "/dashboard" to the role's specific dashboard. */
function RoleHomeRedirect(): JSX.Element {
  const { isAuthenticated, user } = useAuthStore();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <Navigate to={dashboardPathForRole(user?.role)} replace />;
}

function App(): JSX.Element {
  return (
    <BrowserRouter>
      <GlobalMaintenanceBanner />
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/oauth/callback" element={<OAuthCallback />} />
        <Route path="/auth/request-access" element={<RequestAccess />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/security/not-me" element={<SecurityNotMe />} />
        <Route path="/security/new-sign-in" element={<NewSignIn />} />
        <Route path="/email-preferences" element={<EmailPreferences />} />
        <Route path="/accept-invitation" element={<AcceptInvitation />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/maintenance" element={<Maintenance />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy-notice" element={<PrivacyNotice />} />

        {/* Role-bound dashboards — every authenticated screen lives inside MainLayout
            so the topbar/user-menu chrome is consistent across roles. */}
        <Route element={<ProtectedRoute allowedRoles={['manager', 'super_admin']} />}>
          <Route element={<MainLayout />}>
            <Route path="/manager/dashboard" element={<ManagerDashboard />} />
            <Route path="/manager/team" element={<ManagerTeam />} />
            <Route path="/manager/invitations" element={<ManagerInvitations />} />
            <Route path="/manager/leads" element={<AdminLeads />} />
            <Route path="/manager/review-queue" element={<ManagerReviewQueue />} />
            <Route path="/manager/appointments" element={<ManagerAppointments />} />
            <Route path="/manager/deposits" element={<ManagerDeposits />} />
            <Route path="/manager/withdrawals" element={<StaffWithdrawals />} />
            <Route path="/manager/reversals" element={<ManagerReversals />} />
            <Route path="/manager/compliance/alerts" element={<ManagerComplianceAlerts />} />
            <Route path="/manager/compliance/sanctions" element={<ManagerSanctions />} />
            <Route path="/manager/compliance/sars" element={<ManagerSars />} />
            <Route path="/manager/compliance/regulatory-reports" element={<RegulatoryReports />} />
            <Route path="/manager/commitments" element={<ManagerCommitments />} />
            <Route path="/manager/proposal-campaigns" element={<ManagerProposalCampaigns />} />
            <Route path="/manager/cases/:caseId" element={<StaffCaseDetail />} />
            <Route path="/manager/clients/:clientId" element={<ClientDetail />} />
            <Route path="/manager/call-guide/:clientId" element={<CallGuide />} />
            <Route path="/manager/ops/attribute-deposit" element={<StaffAttributeDeposit />} />
            <Route path="/manager/ops/payouts" element={<StaffPayouts />} />
            <Route path="/manager/ops/reconciliation" element={<StaffReconciliation />} />
            <Route path="/manager/ops/client-risk" element={<StaffClientRisk />} />
            <Route path="/manager/ops/redemptions" element={<StaffRedemptions />} />
            <Route path="/manager/offering-documents" element={<AdminOfferingDocuments />} />
            <Route path="/manager/offering-package" element={<ManagerRegDPackage />} />
            <Route path="/manager/compliance/blocked-countries" element={<AdminBlockedCountries />} />
            <Route path="/manager/ops/checks" element={<OperationalReminders />} />
            <Route path="/manager/ops/bank-account-checks" element={<BankAccountChecks />} />
            <Route path="/manager/ops/source-accounts" element={<SourceAccounts />} />
            <Route path="/manager/compliance/transfer-cases" element={<TransferCases />} />
            <Route path="/manager/compliance/suspicion-reports" element={<SuspicionAvisos />} />
            <Route path="/manager/compliance/uma-value" element={<UmaValue />} />
            <Route path="/manager/compliance/blocked-property" element={<OfacBlocking />} />
            <Route path="/manager/compliance/foreign-investment" element={<RnieRegistrations />} />
            <Route path="/manager/compliance/data-requests" element={<ManagerDataRequests />} />
            <Route path="/manager/deposit-reversals" element={<StaffDepositReversals />} />
          </Route>
        </Route>
        <Route element={<ProtectedRoute allowedRoles={['super_admin']} />}>
          <Route element={<MainLayout />}>
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/staff" element={<AdminStaff />} />
            <Route path="/admin/clients" element={<AdminClients />} />
            <Route path="/admin/maintenance" element={<AdminMaintenance />} />
            <Route path="/admin/commitment-configs" element={<AdminCommitmentConfigs />} />
            <Route path="/admin/concentration-accounts" element={<AdminConcentrationAccounts />} />
            <Route path="/admin/email-templates" element={<AdminEmailTemplates />} />
            <Route path="/admin/contract-templates" element={<AdminContractTemplates />} />
            <Route path="/admin/config-health" element={<AdminConfigHealth />} />
            <Route path="/admin/product-access" element={<AdminProductAccess />} />
            <Route path="/admin/products" element={<AdminProducts />} />
            <Route path="/admin/jurisdictions" element={<AdminJurisdictions />} />
            <Route path="/admin/document-wording" element={<AdminDocumentWording />} />
            <Route path="/admin/audit-log-export" element={<AdminAuditLogExport />} />
          </Route>
        </Route>
        <Route element={<ProtectedRoute allowedRoles={['agent']} />}>
          <Route element={<MainLayout />}>
            <Route path="/agent/dashboard" element={<AgentDashboard />} />
            <Route path="/agent/review-queue" element={<AgentReviewQueue />} />
            <Route path="/agent/availability" element={<AgentAvailability />} />
            <Route path="/agent/appointments" element={<AgentAppointments />} />
            <Route path="/agent/deposits" element={<AgentDeposits />} />
            <Route path="/agent/withdrawals" element={<StaffWithdrawals />} />
            <Route path="/agent/reversals" element={<AgentReversals />} />
            <Route path="/agent/commitments" element={<AgentCommitments />} />
            <Route path="/agent/clients" element={<AgentMyClients />} />
            <Route path="/agent/cases/:caseId" element={<StaffCaseDetail />} />
            <Route path="/agent/clients/:clientId" element={<ClientDetail />} />
            <Route path="/agent/call-guide/:clientId" element={<CallGuide />} />
            <Route path="/agent/ops/attribute-deposit" element={<StaffAttributeDeposit />} />
            <Route path="/agent/ops/payouts" element={<StaffPayouts />} />
            <Route path="/agent/ops/reconciliation" element={<StaffReconciliation />} />
            <Route path="/agent/deposit-reversals" element={<StaffDepositReversals />} />
            <Route path="/agent/offering-questions" element={<AgentOfferingQuestions />} />
            {/* Reading the firm's operational reminders is staff-wide (the backend
                admits agents; only signing one off is super_admin, and the page
                hides that control by role). Account checks are agent work too. */}
            <Route path="/agent/ops/checks" element={<OperationalReminders />} />
            <Route path="/agent/ops/bank-account-checks" element={<BankAccountChecks />} />
          </Route>
        </Route>
        <Route element={<ProtectedRoute allowedRoles={['client']} />}>
          <Route element={<MainLayout />}>
            <Route path="/client/dashboard" element={<ClientDashboard />} />
            <Route path="/client/portfolio" element={<ClientPortfolio />} />
            <Route path="/client/onboarding" element={<ClientOnboarding />} />
            {/* Deep link to a specific case (compliance banner → remediation case). */}
            <Route path="/client/onboarding/:caseId" element={<ClientOnboarding />} />
            <Route path="/client/contracts" element={<ClientContracts />} />
            <Route path="/client/contracts/:caseId" element={<ClientContractCase />} />
            <Route path="/client/beneficiaries" element={<ClientBeneficiaries />} />
            <Route path="/client/fund" element={<ClientFund />} />
            <Route path="/client/withdraw" element={<ClientWithdraw />} />
            <Route path="/client/data-requests" element={<ClientDataRequests />} />
            <Route path="/client/products/:productId" element={<ClientProductDetail />} />
            <Route path="/simulator" element={<Simulator />} />
          </Route>
        </Route>

        {/* Generic protected pages — any authenticated role */}
        <Route element={<ProtectedRoute />}>
          <Route element={<MainLayout />}>
            <Route path="/profile" element={<Profile />} />
          </Route>
          {/* Forced credential rotation — outside MainLayout so the
              navbar (with role-bound links) doesn't show during the
              rotation. Reachable for any authenticated user; the
              guard in ProtectedRoute pins the user here when their
              must_change_password flag is set. */}
          <Route path="/auth/change-password" element={<ChangePassword />} />
          {/* Forced second-factor enrolment — outside MainLayout (same as
              change-password) so the role-bound navbar doesn't show during
              the gate. The ProtectedRoute guard pins the user here while
              must_enroll_mfa is set. */}
          <Route path="/auth/mfa-enroll" element={<MfaEnroll />} />
          {/* A client who owes the privacy notice (#289) or the Terms and Conditions (0181) in force is
              pinned here by the ProtectedRoute guard until they accept; one page shows whichever
              of the two they owe, each with its own box. */}
          <Route path="/auth/privacy-notice" element={<AcceptLegalDocuments />} />
          <Route path="/auth/terms" element={<AcceptLegalDocuments />} />
        </Route>

        {/* Backwards-compatible aliases — bounce to the role-specific URL */}
        <Route path="/" element={<RoleHomeRedirect />} />
        <Route path="/dashboard" element={<RoleHomeRedirect />} />

        {/* Catch-all 404. Without this, an unmatched path (e.g. a typo
            like /+*-) renders nothing and the user sees a blank screen
            with no way to recover. The NotFound page offers a CTA back
            to the role dashboard (or /login if anonymous). */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App
